// Core Modules
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// External Modules
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

// Services
import { AccessControlService } from '@core/services/access-control/access-control.service';
import { AuthService } from '@core/services/auth/auth.service';

// Components
import { ChipComponent } from '@shared/components/chip/chip.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { SectionCardComponent } from '@shared/components/section-card/section-card.component';

// Models
import { IUserDetails } from '@shared/models/auth-api-response.model';

// Enums
import { UserProfileFieldsEnum } from '@shared/enum/user-profile-fields.enum';

// Utils
import { ScopeGroup, groupScopes } from './scope-parse.util';

@Component({
  selector: 'app-my-access-modal',
  templateUrl: './my-access-modal.component.html',
  styleUrl: './my-access-modal.component.scss',
  imports: [ChipComponent, EmptyStateComponent, SectionCardComponent, UpperCasePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyAccessModalComponent implements OnInit {
  // Injectors
  private readonly activeModal = inject(NgbActiveModal);
  private readonly accessControl = inject(AccessControlService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  // Fallback-fetch state (used only when the startup cache is empty)
  private readonly fetched = signal<IUserDetails | null>(null);
  public readonly loading = signal(false);
  public readonly error = signal(false);

  // Scope list expand/collapse (collapsed by default — scopes are the advanced surface)
  public readonly expanded = signal(false);

  // Identity from the decrypted Auth0 profile (name/email are not on /auth/me)
  public userEmailName = '';
  public userNameInitials = '';

  // Details: cached startup body first, else the fallback fetch
  public readonly details = computed<IUserDetails | null>(() => this.accessControl.userDetails() ?? this.fetched());
  public readonly roles = computed<string[]>(() => this.details()?.appRoles ?? []);
  public readonly scopes = computed<string[]>(() => this.details()?.scopes ?? []);
  public readonly roleCount = computed<number>(() => this.roles().length);
  public readonly scopeCount = computed<number>(() => this.scopes().length);
  public readonly scopeGroups = computed<ScopeGroup[]>(() => groupScopes(this.scopes()));

  public scopeVerbModifier(verb: string): string {
    const known = ['read', 'write', 'delete'];
    // Check all segments — handles "read", "tableau:read", "read:private", "write:adhoc-sync-activities"
    const match = (verb || '').split(':').find((s) => known.includes(s.toLowerCase()));
    return match ? match.toLowerCase() : 'unknown';
  }

  public ngOnInit(): void {
    const profile = this.auth.getUserProfileBasedOnFields([UserProfileFieldsEnum.EMAIL]);
    this.userEmailName = profile?.email ?? '';
    this.userNameInitials = this.userEmailName ? this.userEmailName.charAt(0).toUpperCase() : '';

    if (!this.accessControl.userDetails()) {
      this.load();
    }
  }

  public load(): void {
    this.loading.set(true);
    this.error.set(false);
    this.accessControl
      .getUserScopes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (details) => {
          this.fetched.set(details);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  public toggleScopes(): void {
    this.expanded.update((value) => !value);
  }

  public close(): void {
    this.activeModal.close();
  }
}
